/**
 * Streaming response handler — ai_stream SSE contract parser.
 *
 * Invoked only on responses with Content-Type: text/event-stream
 * (useChatSend.js routes JSON responses to processNonStreamingResponse instead),
 * so this file consumes the ai_stream contract exclusively.
 *
 * Frame shapes consumed:
 *   data: {contract_version: N}                            — handshake (logged)
 *   data: {choices:[{delta:{content:"..."}}]}              — text delta
 *   data: {usageData:{messagesUsed,limit,resetDate}}       — usage stats
 *   data: {image_preview:{tool_call_id,url,idx,...}}       — partial image
 *   data: {media:[{url,tool_call_id,...}]}                 — final image/media list
 *   data: {widgets|diagrams|circuits|spreadsheets:[...]}   — artifact arrays
 *   data: {toolsUsed:[...]} | {sources:[...]}              — tool history
 *   event: tool_start \n data: {name,turn}                 — tool spinner on
 *   event: tool_result \n data: {name,ok,code?,...}        — tool spinner off
 *   event: tool_error|error|notice \n data: {...}          — error surface
 *   data: [DONE]                                           — terminator
 */
import { devLog, devError } from './devLog';
import { sanitizeAIResponse } from './chatUtils';

export const processStream = async (response, { setMessages, setUser, setIsWebSearching, isWebSearching, setQnaData, setSessions, patchSession, currentSessionId }) => {
  const reader = response.body.getReader();
  const decoder = new TextDecoder();

  // Per-message accumulating state. Re-rendered via setMessages on every mutation.
  const state = {
    text: "",
    media: [],
    widgets: null,
    diagrams: null,
    circuits: null,
    spreadsheets: null,
    sources: null,
    toolsUsed: null,
  };

  setMessages((prev) => [...prev, { role: "ai", text: "", media: null }]);

  // Bridge the ai_stream flat-array `media` shape to the {images, videos} object
  // MediaGallery (Message.js) expects. Partial entries (no `type`) bucket as images.
  const _bucketMedia = (items) => {
    const images = [], videos = [];
    for (const it of items) {
      if (it.type === "video") videos.push(it);
      else images.push(it);
    }
    return { images, videos };
  };

  const flush = () => {
    const out = {
      role: "ai",
      text: sanitizeAIResponse(state.text.trim()),
      media: state.media.length ? _bucketMedia(state.media) : undefined,
      widgets: state.widgets || undefined,
      diagrams: state.diagrams || undefined,
      circuits: state.circuits || undefined,
      spreadsheets: state.spreadsheets || undefined,
      sources: state.sources || undefined,
      toolsUsed: state.toolsUsed || undefined,
    };
    setMessages((prev) => {
      const copy = [...prev];
      copy[copy.length - 1] = out;
      return copy;
    });
  };

  // Partial-image upsert: replace by tool_call_id; keep latest idx (higher resolution).
  const upsertPartial = (item) => {
    const tcid = item.tool_call_id;
    const existingIdx = state.media.findIndex((m) => m.tool_call_id === tcid);
    const next = { url: item.url, partial: true, tool_call_id: tcid, idx: item.idx };
    if (existingIdx === -1) state.media.push(next);
    else if ((state.media[existingIdx].idx ?? -1) <= (item.idx ?? 0)) state.media[existingIdx] = next;
  };

  // Final media: replace any partial sharing a tool_call_id with the final.
  const settleMedia = (items) => {
    if (!Array.isArray(items)) return;
    for (const it of items) {
      const tcid = it.tool_call_id;
      const existingIdx = tcid ? state.media.findIndex((m) => m.tool_call_id === tcid) : -1;
      const finalEntry = { ...it, partial: false };
      if (existingIdx !== -1) state.media[existingIdx] = finalEntry;
      else state.media.push(finalEntry);
    }
  };

  const handleData = (eventName, data) => {
    if (data === '[DONE]') return;
    let parsed;
    try { parsed = JSON.parse(data); } catch (e) { return; }

    if (eventName === 'tool_start') {
      // Append a running entry to the message's toolsUsed list so the
      // ToolSteps component shows shimmer animation while the tool runs.
      // Matches mobile contract (chat_provider.dart:669 / tool_steps.dart).
      // Keep the global isWebSearching flag for backwards-compat with the
      // older single-spinner code path in Chatty.js until that's retired.
      const name = parsed.name || '';
      const turn = typeof parsed.turn === 'number' ? parsed.turn : 0;
      const args = parsed.arguments && typeof parsed.arguments === 'object'
        ? parsed.arguments : undefined;
      const entry = { name, running: true, turn };
      if (args) entry.arguments = args;
      state.toolsUsed = [...(state.toolsUsed || []), entry];
      if (name === 'web_search') setIsWebSearching(true);
      flush();
      return;
    }
    if (eventName === 'tool_result') {
      // Flip the most recent running entry for this tool to done and stamp
      // its tool_call_id. The rich `toolsUsed` frame that the backend emits
      // next (app.py emits tool_result before toolsUsed) is merged onto this
      // exact entry by that id.
      const name = parsed.name || '';
      const ok = parsed.ok === true;
      const toolCallId = parsed.tool_call_id;
      const list = [...(state.toolsUsed || [])];
      for (let i = list.length - 1; i >= 0; i--) {
        const t = list[i];
        if (t && t.name === name && t.running === true) {
          list[i] = { ...t, running: false, ok, tool_call_id: toolCallId };
          break;
        }
      }
      state.toolsUsed = list;
      if (name === 'web_search') setIsWebSearching(false);
      flush();
      return;
    }
    if (eventName === 'tool_error') {
      // Flip the matching running entry to error state so ToolSteps
      // renders a red row with the error message.
      const name = parsed.name || '';
      const list = [...(state.toolsUsed || [])];
      for (let i = list.length - 1; i >= 0; i--) {
        const t = list[i];
        if (t && t.name === name) {
          list[i] = {
            ...t, running: false, ok: false,
            error_message: parsed.message || parsed.code || 'failed',
          };
          break;
        }
      }
      state.toolsUsed = list;
      if (name === 'web_search') setIsWebSearching(false);
      devError(`SSE tool_error:`, parsed);
      flush();
      return;
    }
    if (eventName === 'error' || eventName === 'notice') {
      setIsWebSearching(false);
      devError(`SSE ${eventName}:`, parsed);
      return;
    }

    if (parsed.contract_version !== undefined) {
      devLog(`ai_stream contract_version=${parsed.contract_version}`);
      return;
    }
    if (parsed.usageData) {
      const { messagesUsed, limit, resetDate } = parsed.usageData;
      setUser((prev) => ({
        ...prev,
        'custom:messagesUsed': String(messagesUsed),
        'custom:limit': String(limit),
        'custom:resetDate': resetDate,
      }));
      return;
    }
    if (parsed.image_preview) { upsertPartial(parsed.image_preview); flush(); return; }
    if (parsed.media)         { settleMedia(parsed.media);          flush(); return; }
    if (parsed.widgets)       { state.widgets = parsed.widgets;     flush(); return; }
    if (parsed.diagrams)      { state.diagrams = parsed.diagrams;   flush(); return; }
    if (parsed.circuits)      { state.circuits = parsed.circuits;   flush(); return; }
    if (parsed.spreadsheets)  { state.spreadsheets = parsed.spreadsheets; flush(); return; }
    if (parsed.sources)       { state.sources = parsed.sources;     flush(); return; }
    if (parsed.toolsUsed) {
      // Merge, never replace. The backend emits one rich `toolsUsed` frame
      // per tool (app.py:4264), each a single-element array — a plain replace
      // would drop every tool but the last on a multi-tool turn. Match each
      // rich entry onto its accumulated entry by tool_call_id. An entry with
      // no id match is inserted: a /history reload delivers the whole
      // persisted array into an empty list, so every entry inserts there.
      const list = [...(state.toolsUsed || [])];
      for (const rich of parsed.toolsUsed) {
        if (!rich || typeof rich !== 'object') continue;
        const tcid = rich.tool_call_id;
        const idx = tcid
          ? list.findIndex((t) => t && t.tool_call_id === tcid)
          : -1;
        if (idx !== -1) list[idx] = { ...list[idx], ...rich };
        else list.push(rich);
      }
      state.toolsUsed = list;
      flush();
      return;
    }
    // Mirror non-stream handler's QnA wiring: ai_stream emits
    // `user_input_request` SSE frame when the AI calls ask_user_input.
    // Same shape as nonStreamingHandler.js:70 → { questions: <array> }.
    if (parsed.user_input_request && setQnaData) {
      setQnaData({ questions: parsed.user_input_request });
      return;
    }
    // Localized title update from ai_stream. Sidebar reads from App.js's
    // chatSessions (via ChatInputContext.patchSession), while ChatBreadcrumb
    // reads from useChatSessions.sessions (via setSessions). Both stores
    // mirror the same conceptual list — update both so sidebar AND breadcrumb
    // reflect the new title without a full refetch.
    if (parsed.sessionTitle && currentSessionId) {
      if (typeof patchSession === 'function') {
        patchSession(currentSessionId, { title: parsed.sessionTitle });
      }
      if (typeof setSessions === 'function') {
        setSessions(prev => prev.map(s =>
          s.session_id === currentSessionId ? { ...s, title: parsed.sessionTitle } : s
        ));
      }
      return;
    }

    const content = parsed.choices?.[0]?.delta?.content;
    if (typeof content === 'string' && content) {
      state.text += content;
      flush();
    }
  };

  // SSE wire parser: line-buffered. `event:` sets the name for the next `data:`.
  let buffer = "";
  let currentEvent = null;
  const consumeLine = (line) => {
    if (line === "") { currentEvent = null; return; }
    if (line.startsWith(":")) return;
    if (line.startsWith("event: ")) { currentEvent = line.slice(7).trim(); return; }
    if (line.startsWith("data: ")) { handleData(currentEvent, line.slice(6)); currentEvent = null; return; }
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let nlIdx;
    while ((nlIdx = buffer.indexOf('\n')) !== -1) {
      const line = buffer.slice(0, nlIdx).replace(/\r$/, '');
      buffer = buffer.slice(nlIdx + 1);
      consumeLine(line);
    }
  }
  if (buffer.length) consumeLine(buffer.replace(/\r$/, ''));

  if (isWebSearching) setIsWebSearching(false);
  flush();

  // No post-stream refreshSessions(): backend now emits a `sessionTitle` SSE
  // frame mid-stream (ai_stream/app.py after _save_exchange) so the frontend
  // updates exactly one row in the local sessions array via setSessions.
  // A full refetch here would re-render the entire sidebar and disturb other
  // rows, which is what we are explicitly avoiding.

  return state.text;
};
